import React, { useCallback } from "react";

import {
  CircularProgress,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { days } from "../../types";
import { PublicationSchema } from "../../types";
import RemoveButton from "../RemoveButton";

import PublicationDaysSelect from "./PublicationDaysSelect";
import {
  PublicationPricesPerDayEditable,
  WeekdayPricesEditable,
} from "./types";

export interface PriceListRowProps {
  data: PublicationPricesPerDayEditable;
  isEditing: boolean;
  publications: PublicationSchema[];
  isLoadingPublications: boolean;
  onUpdate?: (updatedData: PublicationPricesPerDayEditable) => void;
  onRemove?: () => void;
  onRemoveGroup?: (daysToRemove: string[]) => void;
  disabledDays?: string[];
}

export interface GroupedDayPrices {
  weekdays: string[];
  simple_width?: number;
  double_width?: number;
  memorial?: number;
  thanks?: number;
  discount_pct?: number;
}

const PriceListRow: React.FC<PriceListRowProps> = ({
  data,
  isEditing,
  publications,
  isLoadingPublications,
  onUpdate,
  onRemove,
  onRemoveGroup,
  disabledDays,
}) => {
  const groupedDays = data.days_prices.reduce<GroupedDayPrices[]>(
    (groups, day) => {
      const existingGroup = groups.find(
        (group) =>
          group.simple_width === day.simple_width &&
          group.double_width === day.double_width &&
          group.memorial === day.memorial &&
          group.thanks === day.thanks &&
          group.discount_pct === day.discount_pct,
      );

      if (existingGroup) {
        existingGroup.weekdays.push(day.weekday);
      } else {
        groups.push({
          simple_width: day.simple_width,
          double_width: day.double_width,
          memorial: day.memorial,
          thanks: day.thanks,
          discount_pct: day.discount_pct,
          weekdays: [day.weekday],
        });
      }

      return groups;
    },
    [],
  );

  const updateGroups = useCallback(
    (newGroups: GroupedDayPrices[]) => {
      if (onUpdate) {
        const updatedDaysPrices = newGroups.flatMap((group) =>
          group.weekdays.map((weekday) => ({
            weekday,
            simple_width: group.simple_width,
            double_width: group.double_width,
            memorial: group.memorial,
            thanks: group.thanks,
            discount_pct: group.discount_pct,
          })),
        );

        onUpdate({
          ...data,
          days_prices: updatedDaysPrices,
        });
      }
    },
    [data, onUpdate],
  );

  const handleValueChange = useCallback(
    (
      groupIndex: number,
      field: keyof Omit<WeekdayPricesEditable, "weekday">,
      valueString: string,
    ) => {
      const newGroups = [...groupedDays];
      const currentGroup = newGroups[groupIndex];
      if (!currentGroup) return;

      const value =
        valueString === ""
          ? undefined
          : Number(valueString.replaceAll(",", "."));
      if (value !== undefined && isNaN(value)) return;

      const updatedGroup: GroupedDayPrices = {
        weekdays: [...currentGroup.weekdays],
        simple_width:
          field === "simple_width" ? value : currentGroup.simple_width,
        double_width:
          field === "double_width" ? value : currentGroup.double_width,
        memorial: field === "memorial" ? value : currentGroup.memorial,
        thanks: field === "thanks" ? value : currentGroup.thanks,
        discount_pct:
          field === "discount_pct" ? value : currentGroup.discount_pct,
      };
      newGroups[groupIndex] = updatedGroup;
      updateGroups(newGroups);
    },
    [groupedDays, updateGroups],
  );

  const handleDaysChange = useCallback(
    (groupIndex: number, newWeekdays: string[]) => {
      const newGroups = [...groupedDays];
      const currentGroup = newGroups[groupIndex];
      if (!currentGroup) return;

      const weekdays =
        newWeekdays.length > 0 ? newWeekdays.filter(Boolean) : [""];
      const updatedGroup: GroupedDayPrices = {
        weekdays,
        simple_width: currentGroup.simple_width,
        double_width: currentGroup.double_width,
        memorial: currentGroup.memorial,
        thanks: currentGroup.thanks,
        discount_pct: currentGroup.discount_pct,
      };

      newGroups[groupIndex] = updatedGroup;
      updateGroups(newGroups);
    },
    [groupedDays, updateGroups],
  );

  const renderRow = (groupedDay: GroupedDayPrices, groupIndex: number) => {
    const weekdaysKey = groupedDay.weekdays.filter(Boolean).sort().join("-");

    return (
      <TableRow key={`${data.publication}-${weekdaysKey}-${groupIndex}`}>
        <TableCell sx={{ py: 0 }}>
          {isLoadingPublications ? (
            <CircularProgress size={16} />
          ) : (
            <Typography py={2} variant="body2">
              {publications.find((p) => p.abbreviation === data.publication)
                ?.name || data.publication}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ py: 0 }}>
          {isEditing ? (
            <PublicationDaysSelect
              disabledDays={disabledDays}
              selectedDays={groupedDay.weekdays}
              onChange={(newDays) => handleDaysChange(groupIndex, newDays)}
            />
          ) : (
            <Typography py={2} variant="body2">
              {(() => {
                const allWeekdays = [
                  "monday",
                  "tuesday",
                  "wednesday",
                  "thursday",
                  "friday",
                  "saturday",
                  "sunday",
                ];
                const filteredDays = groupedDay.weekdays.filter(Boolean);

                if (
                  filteredDays.length === allWeekdays.length &&
                  allWeekdays.every((day) => filteredDays.includes(day))
                ) {
                  return "Alla dagar";
                }

                return filteredDays
                  .map((day) => days[day as keyof typeof days])
                  .join(", ");
              })()}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ py: 0 }}>
          {isEditing ? (
            <TextField
              key={`simple-${weekdaysKey}-${groupIndex}`}
              defaultValue={groupedDay.simple_width ?? ""}
              size="small"
              slotProps={{
                input: {
                  endAdornment: "kr",
                },
              }}
              sx={(theme) => ({
                bgcolor: theme.palette.mode === "light" ? "white" : undefined,
              })}
              onBlur={(e) =>
                handleValueChange(groupIndex, "simple_width", e.target.value)
              }
            />
          ) : (
            `${groupedDay.simple_width ?? ""} kr`
          )}
        </TableCell>
        <TableCell sx={{ py: 0 }}>
          {isEditing ? (
            <TextField
              key={`double-${weekdaysKey}-${groupIndex}`}
              defaultValue={groupedDay.double_width ?? ""}
              size="small"
              slotProps={{ input: { endAdornment: "kr" } }}
              sx={(theme) => ({
                bgcolor: theme.palette.mode === "light" ? "white" : undefined,
              })}
              onBlur={(e) =>
                handleValueChange(groupIndex, "double_width", e.target.value)
              }
            />
          ) : (
            `${groupedDay.double_width ?? ""} kr`
          )}
        </TableCell>
        <TableCell sx={{ py: 0 }}>
          {isEditing ? (
            <TextField
              key={`memorial-${weekdaysKey}-${groupIndex}`}
              defaultValue={groupedDay.memorial ?? ""}
              size="small"
              slotProps={{ input: { endAdornment: "kr" } }}
              sx={(theme) => ({
                bgcolor: theme.palette.mode === "light" ? "white" : undefined,
              })}
              onBlur={(e) =>
                handleValueChange(groupIndex, "memorial", e.target.value)
              }
            />
          ) : (
            `${groupedDay.memorial ?? ""} kr`
          )}
        </TableCell>
        <TableCell sx={{ py: 0 }}>
          {isEditing ? (
            <TextField
              key={`thanks-${weekdaysKey}-${groupIndex}`}
              defaultValue={groupedDay.thanks ?? ""}
              size="small"
              slotProps={{ input: { endAdornment: "kr" } }}
              sx={(theme) => ({
                bgcolor: theme.palette.mode === "light" ? "white" : undefined,
              })}
              onBlur={(e) =>
                handleValueChange(groupIndex, "thanks", e.target.value)
              }
            />
          ) : (
            `${groupedDay.thanks ?? ""} kr`
          )}
        </TableCell>
        <TableCell sx={{ py: 0 }}>
          {isEditing ? (
            <TextField
              key={`discount-${weekdaysKey}-${groupIndex}`}
              defaultValue={groupedDay.discount_pct ?? ""}
              size="small"
              slotProps={{ input: { endAdornment: "%" } }}
              sx={(theme) => ({
                bgcolor: theme.palette.mode === "light" ? "white" : undefined,
              })}
              onBlur={(e) =>
                handleValueChange(groupIndex, "discount_pct", e.target.value)
              }
            />
          ) : (
            `${groupedDay.discount_pct ?? ""} %`
          )}
        </TableCell>
        {isEditing && (onRemove || onRemoveGroup) && (
          <TableCell sx={{ py: 0 }}>
            <RemoveButton
              sx={{ color: "text.secondary" }}
              onClick={() => {
                if (onRemoveGroup && groupedDays.length > 1) {
                  // If there are multiple groups, remove only this specific group
                  onRemoveGroup(groupedDay.weekdays);
                } else if (onRemove) {
                  // If this is the only group, remove the entire row
                  onRemove();
                }
              }}
            />
          </TableCell>
        )}
      </TableRow>
    );
  };

  return (
    <>{groupedDays.map((groupedDay, index) => renderRow(groupedDay, index))}</>
  );
};

export default PriceListRow;
